import randomUUID from '@/utils/uuid'
import { SchemaType } from '@/constants/schema'
import { resolveRefPath } from '@/shared/utils/workflowSpec'

const isRef = (val) => val != null && typeof val === 'object' && typeof val.$ref === 'string'

const resolveRefToDisplayName = (ref, root) => {
  // For decision refs, return the slug — descriptions can repeat, slugs can't.
  if (ref.$ref.startsWith('#/decisions/')) {
    const segments = ref.$ref.slice(2).split('/')

    return segments[segments.length - 1]
  }

  // For event refs (not domain-event schema refs), return the slug for the
  // same reason. `#/schemas/domainEvents/...` is a schema ref, not an event.
  // External-BC events are prefixed with their BC key so lookups stay unique across BCs.
  if (ref.$ref.includes('/domainEvents/') && !ref.$ref.includes('/schemas/domainEvents/')) {
    const segments = ref.$ref.slice(2).split('/')
    const slug = segments[segments.length - 1]
    if (segments[0] === 'externalBoundedContexts' && segments.length >= 4) {
      return `${segments[1]}::${slug}`
    }

    return slug
  }

  const resolved = resolveRefPath(ref.$ref, root)
  if (!resolved) {
    return ref.$ref
  }

  if (resolved.event) {
    return resolved.event
  }

  const segments = ref.$ref.slice(2).split('/')
  const key = segments[segments.length - 1]

  return pascalCaseToDisplayName(key)
}

const pascalCaseToDisplayName = (pascalCase) => {
  if (!pascalCase) {
    return ''
  }

  return pascalCase
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
}

const resolveFieldRefs = (fields, root) => {
  if (!fields) {
    return
  }
  for (const field of fields) {
    if (isRef(field.relatedEntity)) {
      field.relatedEntity = resolveRefToDisplayName(field.relatedEntity, root)
    }
    if (field.fields) {
      resolveFieldRefs(field.fields, root)
    }
  }
}

const resolveEventsRefs = (eventsObj, root) => {
  if (!eventsObj) {
    return
  }
  for (const event of Object.values(eventsObj)) {
    if (isRef(event.command)) {
      event.command = resolveRefToDisplayName(event.command, root)
    }
    if (event.domainEventSchema) {
      if (isRef(event.domainEventSchema) || event.domainEventSchema.$ref) {
        const name = resolveRefToDisplayName(event.domainEventSchema, root)
        event.domainEventSchema = { name, description: event.domainEventSchema.description }
      }
    }
    if (isRef(event.aggregateRoot)) {
      event.aggregateRoot = resolveRefToDisplayName(event.aggregateRoot, root)
    }
    if (event.readModels) {
      event.readModels = event.readModels.map((rm) => {
        if (isRef(rm) || rm.$ref) {
          const name = resolveRefToDisplayName(rm, root)
          const result = { name }
          if (rm.array) {
            result.array = true
          }
          if (rm.description) {
            result.description = rm.description
          }

          return result
        }

        return rm
      })
    }
    if (event.follows) {
      event.follows = event.follows.map((f) => {
        if (isRef(f)) {
          return resolveRefToDisplayName(f, root)
        }

        return f
      })
    }
    if (event.conditions) {
      for (const cond of event.conditions) {
        if (isRef(cond.after)) {
          cond.after = resolveRefToDisplayName(cond.after, root)
        }
      }
    }
  }
}

const resolveSchemasRefs = (schemasObj, root) => {
  if (!schemasObj) {
    return
  }

  for (const schema of Object.values(schemasObj.commands || {})) {
    resolveFieldRefs(schema.fields, root)
  }
  for (const schema of Object.values(schemasObj.queries || {})) {
    if (isRef(schema.entity)) {
      schema.entity = resolveRefToDisplayName(schema.entity, root)
    }
    resolveFieldRefs(schema.fields, root)
  }
  for (const schema of Object.values(schemasObj.domainEvents || {})) {
    if (isRef(schema.entity)) {
      schema.entity = resolveRefToDisplayName(schema.entity, root)
    }
    resolveFieldRefs(schema.fields, root)
  }
  for (const schema of Object.values(schemasObj.entities || {})) {
    resolveFieldRefs(schema.fields, root)
  }
  for (const schema of Object.values(schemasObj.valueObjects || {})) {
    resolveFieldRefs(schema.fields, root)
  }
}

const collectReadModelArrayNames = (events, sink) => {
  if (!events) {
    return
  }
  const eventArr = Array.isArray(events) ? events : Object.values(events)
  for (const event of eventArr) {
    for (const rm of event.readModels || []) {
      if (rm.array && rm.name) {
        sink.add(rm.name)
      }
    }
  }
}

const mergeArrayIntoQueries = (queriesObj, arrayNames) => {
  if (!queriesObj) {
    return
  }
  for (const [key, query] of Object.entries(queriesObj)) {
    const displayName = pascalCaseToDisplayName(key)
    if (arrayNames.has(displayName) && !query.array) {
      query.array = true
    }
  }
}

// Backward compatibility: old exports are holding the cardinality on card, not schema
const propagateReadModelArrayToQueries = (data) => {
  const arrayNames = new Set()

  collectReadModelArrayNames(data.domainEvents, arrayNames)
  for (const bc of Object.values(data.externalBoundedContexts || {})) {
    collectReadModelArrayNames(bc.domainEvents, arrayNames)
  }

  if (arrayNames.size === 0) {
    return
  }

  mergeArrayIntoQueries(data.schemas?.queries, arrayNames)
  for (const bc of Object.values(data.externalBoundedContexts || {})) {
    mergeArrayIntoQueries(bc.schemas?.queries, arrayNames)
  }
}

const resolveDecisionsRefs = (decisions, root) => {
  if (!decisions) {
    return
  }
  const entries = Array.isArray(decisions) ? decisions : Object.values(decisions)
  for (const decision of entries) {
    if (isRef(decision.follows)) {
      decision.follows = resolveRefToDisplayName(decision.follows, root)
    }
  }
}

const resolveAllRefs = (data) => {
  resolveEventsRefs(data.domainEvents, data)
  resolveSchemasRefs(data.schemas, data)
  resolveDecisionsRefs(data.decisions, data)

  for (const bc of Object.values(data.externalBoundedContexts || {})) {
    resolveEventsRefs(bc.domainEvents, data)
    resolveSchemasRefs(bc.schemas, data)
  }

  propagateReadModelArrayToQueries(data)
}

const mapToArray = (obj, nameField, nameTransform = pascalCaseToDisplayName) => {
  if (!obj || typeof obj !== 'object') {
    return []
  }

  return Object.entries(obj).map(([key, value]) => ({
    [nameField]: nameTransform(key),
    ...value
  }))
}

const ensureType = (schemas, type) =>
  schemas.map((s) => s.type ? s : { ...s, type })

const normalizeSchemasToArrays = (schemasObj) => {
  if (!schemasObj) {
    return schemasObj
  }
  const result = {}
  if (schemasObj.commands && typeof schemasObj.commands === 'object' && !Array.isArray(schemasObj.commands)) {
    result.commands = ensureType(mapToArray(schemasObj.commands, 'name'), 'Command')
  } else if (schemasObj.commands) {
    result.commands = ensureType(schemasObj.commands, 'Command')
  }
  if (schemasObj.queries && typeof schemasObj.queries === 'object' && !Array.isArray(schemasObj.queries)) {
    result.queries = ensureType(mapToArray(schemasObj.queries, 'name'), 'Query')
  } else if (schemasObj.queries) {
    result.queries = ensureType(schemasObj.queries, 'Query')
  }
  if (schemasObj.entities && typeof schemasObj.entities === 'object' && !Array.isArray(schemasObj.entities)) {
    result.entities = ensureType(mapToArray(schemasObj.entities, 'name'), 'Entity')
  } else if (schemasObj.entities) {
    result.entities = ensureType(schemasObj.entities, 'Entity')
  }
  if (schemasObj.domainEvents && typeof schemasObj.domainEvents === 'object' && !Array.isArray(schemasObj.domainEvents)) {
    result.domainEvents = ensureType(mapToArray(schemasObj.domainEvents, 'name'), SchemaType.DOMAIN_EVENT)
  } else if (schemasObj.domainEvents) {
    result.domainEvents = ensureType(schemasObj.domainEvents, SchemaType.DOMAIN_EVENT)
  }
  if (schemasObj.valueObjects && typeof schemasObj.valueObjects === 'object' && !Array.isArray(schemasObj.valueObjects)) {
    result.valueObjects = ensureType(mapToArray(schemasObj.valueObjects, 'name'), 'ValueObject')
  } else if (schemasObj.valueObjects) {
    result.valueObjects = ensureType(schemasObj.valueObjects, 'ValueObject')
  }

  return Object.keys(result).length > 0 ? result : undefined
}

const normalizeEventsMap = (eventsObj, bcKey = '') => {
  if (!eventsObj || typeof eventsObj !== 'object' || Array.isArray(eventsObj)) {
    return eventsObj
  }

  // Prefix slugs from external BCs so lookups stay unique across BCs. Top-level
  // events keep the bare slug; refs into them resolve the same way.
  return Object.entries(eventsObj).map(([slug, value]) => ({
    slug: bcKey ? `${bcKey}::${slug}` : slug,
    event: value.event || pascalCaseToDisplayName(slug),
    ...value
  }))
}

const normalizeMaps = (data) => {
  if (data.domainEvents && typeof data.domainEvents === 'object' && !Array.isArray(data.domainEvents)) {
    data.domainEvents = normalizeEventsMap(data.domainEvents)
  }

  if (data.decisions && typeof data.decisions === 'object' && !Array.isArray(data.decisions)) {
    // Keep the slug on each entry so lookups can disambiguate same-named decisions.
    data.decisions = Object.entries(data.decisions).map(([slug, value]) => ({
      slug,
      ...value
    }))
  }

  data.schemas = normalizeSchemasToArrays(data.schemas)

  if (data.externalBoundedContexts && typeof data.externalBoundedContexts === 'object' && !Array.isArray(data.externalBoundedContexts)) {
    data.externalBoundedContexts = Object.entries(data.externalBoundedContexts).map(([key, value]) => {
      const bc = { name: pascalCaseToDisplayName(key), ...value }
      if (bc.domainEvents && typeof bc.domainEvents === 'object' && !Array.isArray(bc.domainEvents)) {
        bc.domainEvents = normalizeEventsMap(bc.domainEvents, key)
      }
      bc.schemas = normalizeSchemasToArrays(bc.schemas)

      return bc
    })
  }
}

/**
 * Import a bounded-context JSON export back into the workflow format
 * expected by the existing importWorkflow pipeline.
 *
 * All IDs are regenerated; internal references are remapped.
 *
 * @param {object|string} jsonInput - the exported JSON (version 1 format)
 * @param {object|null} cardTypesMap - optional mapping for resolving cardTypeIds in the
 *   destination workflow. Supported keys:
 *   - DomainModelRole string (e.g. "Command", "AggregateRoot") → cardTypeId
 *   - "__title:<Title>" (e.g. "__title:User Story", "__title:Problem") → cardTypeId
 *     for custom card types matched by their exported type name
 *   - "__default__" → fallback cardTypeId when no role or title match is found
 */
export const importBoundedContextJson = (jsonInput, cardTypesMap = null) => {
  const data = typeof jsonInput === 'string' ? JSON.parse(jsonInput) : jsonInput

  // Pre-process: resolve $refs while maps are intact, then normalize to arrays
  const hasMaps = data.domainEvents && typeof data.domainEvents === 'object' && !Array.isArray(data.domainEvents)
  if (hasMaps) {
    resolveAllRefs(data)
    normalizeMaps(data)
  }

  return importWorkflow(data, cardTypesMap)
}

const importWorkflow = (data, cardTypesMap = null) => {
  const RESERVED = new Set(['start'])

  // Name → UUID maps
  const laneIds = new Map()
  const bcIds = new Map()
  const groupIds = new Map()
  const schemaIds = new Map()
  const eventIds = new Map()

  const resolve = (map, name) => {
    if (!name || RESERVED.has(name)) {
      return name
    }
    if (!map.has(name)) {
      map.set(name, randomUUID())
    }

    return map.get(name)
  }

  // Collect all bounded context names
  const currentBcName = data.boundedContext
  const allBcNames = [
    currentBcName,
    ...(data.externalBoundedContexts || []).map((bc) => bc.name)
  ].filter((name) => name && name !== 'Unassigned')

  // Phase 1: Pre-register all names → stable UUIDs
  for (const name of allBcNames) {
    resolve(bcIds, name)
  }

  for (const laneName of data.roles || data.lanes || []) {
    resolve(laneIds, laneName)
  }
  for (const group of data.groups || []) {
    resolve(groupIds, group.name)
  }

  const hasSchema = (s) => 'fields' in s
  const virtualSchemaNames = new Set()

  const registerSchema = (s) => {
    if (hasSchema(s)) {
      resolve(schemaIds, s.name)
    } else {
      virtualSchemaNames.add(s.name)
    }
  }

  const registerSchemas = (schemasSection) => {
    if (!schemasSection) {
      return
    }
    for (const s of schemasSection.commands || []) {
      registerSchema(s)
    }
    for (const s of schemasSection.queries || []) {
      registerSchema(s)
    }
    for (const s of schemasSection.domainEvents || []) {
      registerSchema(s)
    }
    for (const s of schemasSection.entities || []) {
      registerSchema(s)
    }
    for (const s of schemasSection.valueObjects || []) {
      resolve(schemaIds, s.name)
    }
  }

  registerSchemas(data.schemas)
  for (const extBc of data.externalBoundedContexts || []) {
    registerSchemas(extBc.schemas)
  }

  // Pre-register schema and event names referenced by all events (current + external BCs)
  const allExportedEvents = [
    ...(data.domainEvents || []),
    ...(data.externalBoundedContexts || []).flatMap((bc) => bc.domainEvents || [])
  ]
  for (const event of allExportedEvents) {
    resolve(eventIds, event.slug || event.event)
    if (event.command && !virtualSchemaNames.has(event.command)) {
      resolve(schemaIds, event.command)
    }
    const domainEventName = event.domainEventSchema?.name || (typeof event.domainEventSchema === 'string' ? event.domainEventSchema : null)
    if (domainEventName && !virtualSchemaNames.has(domainEventName)) {
      resolve(schemaIds, domainEventName)
    }
    if (event.aggregateRoot && !virtualSchemaNames.has(event.aggregateRoot)) {
      resolve(schemaIds, event.aggregateRoot)
    }
    for (const rm of event.readModels || []) {
      if (!virtualSchemaNames.has(rm.name)) {
        resolve(schemaIds, rm.name)
      }
    }
  }

  // Reserve a stable gateway id for every (parent, description) pair we'll see —
  // from the decisions[] map (covers dangling ones) and from event conditions.
  const gatewayIds = new Map()
  const resolveGateway = (followsName, description) => {
    const key = `${followsName}::${description}`
    if (!gatewayIds.has(key)) {
      gatewayIds.set(key, randomUUID())
    }

    return gatewayIds.get(key)
  }
  for (const decision of data.decisions || []) {
    resolveGateway(decision.follows, decision.description)
  }
  for (const event of allExportedEvents) {
    for (const cond of event.conditions || []) {
      resolveGateway(cond.after, cond.if)
    }
  }

  // Assign decision gateway ids up front (before conditions are processed) so chained
  // $refs resolve to the precise sibling. Same-named siblings share a (parent, description)
  // key: the first reuses the reserved id, each extra gets a fresh one, recorded by slug.
  const decisionSlugToGatewayId = new Map()
  const decisionGatewayIdByEntry = new Map()
  const claimedDecisionKeys = new Set()
  for (const decision of data.decisions || []) {
    const key = `${decision.follows}::${decision.description}`
    const gatewayId = claimedDecisionKeys.has(key) ? randomUUID() : gatewayIds.get(key)
    claimedDecisionKeys.add(key)
    decisionGatewayIdByEntry.set(decision, gatewayId)
    if (decision.slug) {
      decisionSlugToGatewayId.set(decision.slug, gatewayId)
    }
  }

  // Phase 2: Reconstruct schemas (deduplicated by name)
  const schemas = []
  const seenSchemas = new Set()

  const processSchemaSection = (schemasSection, bcName) => {
    if (!schemasSection) {
      return
    }
    const allSchemas = [
      ...(schemasSection.commands || []),
      ...(schemasSection.queries || []),
      ...(schemasSection.domainEvents || []),
      ...(schemasSection.entities || []).map((s) => s.boundedContext ? s : { ...s, boundedContext: bcName }),
      ...(schemasSection.valueObjects || []).map((s) => s.boundedContext ? s : { ...s, boundedContext: bcName })
    ]
    for (const schema of allSchemas) {
      if (seenSchemas.has(schema.name)) {
        continue
      }
      seenSchemas.add(schema.name)
      if (!hasSchema(schema) && (schema.type === SchemaType.COMMAND || schema.type === SchemaType.QUERY || schema.type === SchemaType.ENTITY || schema.type === SchemaType.DOMAIN_EVENT)) {
        continue
      }
      schemas.push(reverseSchema(schema, { schemaIds, bcIds }))
    }
  }

  processSchemaSection(data.schemas, currentBcName)
  for (const extBc of data.externalBoundedContexts || []) {
    const isUnassigned = extBc.name === 'Unassigned'
    processSchemaSection(extBc.schemas, isUnassigned ? null : extBc.name)
  }

  // Create minimal schemas for names referenced by external events but not in any schema section
  for (const extBc of data.externalBoundedContexts || []) {
    for (const event of extBc.domainEvents || []) {
      if (event.command && !seenSchemas.has(event.command)) {
        seenSchemas.add(event.command)
        schemas.push({ id: schemaIds.get(event.command), name: event.command, type: SchemaType.COMMAND, fields: [] })
      }
      const extDeName = event.domainEventSchema?.name || (typeof event.domainEventSchema === 'string' ? event.domainEventSchema : null)
      if (extDeName && !seenSchemas.has(extDeName)) {
        seenSchemas.add(extDeName)
        schemas.push({
          id: schemaIds.get(extDeName),
          name: extDeName,
          type: SchemaType.DOMAIN_EVENT,
          fields: []
        })
      }
      if (event.aggregateRoot && !seenSchemas.has(event.aggregateRoot)) {
        seenSchemas.add(event.aggregateRoot)
        schemas.push({
          id: schemaIds.get(event.aggregateRoot),
          name: event.aggregateRoot,
          type: SchemaType.ENTITY,
          boundedContext: bcIds.get(extBc.name),
          fields: []
        })
      }
    }
  }

  // Phase 3: Build gateway records (from conditions, then dangling decisions) and the events themselves.
  const domainEvents = []
  const createdGateways = new Set()

  // Returns true when `afterKey` identifies the same gateway that `cond`
  // describes. Compares by gateway ID when slugs are available (handles
  // same-description gateways in one chain); falls back to bare-description
  // equality for legacy exports.
  const afterTargetsCond = (afterKey, cond) => {
    if (!afterKey || afterKey === 'start' || !cond) {
      return false
    }
    const decEntry = (data.decisions || []).find((d) => d.slug === afterKey)
    if (decEntry) {
      const afterGwId = gatewayIds.get(`${decEntry.follows}::${decEntry.description}`)
      const refGwId = gatewayIds.get(`${cond.after}::${cond.if}`)

      return afterGwId !== undefined && afterGwId === refGwId
    }

    return afterKey === cond.if
  }

  const resolveGatewayAfter = (afterName, event) => {
    if (afterName === 'start') {
      return 'start'
    }

    const eventId = eventIds.get(afterName)
    if (eventId) {
      return eventId
    }

    // Precise per-slug id: disambiguates same-named siblings the composite key below would merge.
    if (decisionSlugToGatewayId.has(afterName)) {
      return decisionSlugToGatewayId.get(afterName)
    }

    // Chained inner gateway: find the sibling in this event's chain whose
    // `if` matches afterName, then resolve via the composite key for a
    // precise per-event lookup. Description-only lookups across events
    // would collide when two events share a gateway description.
    const sibling = (event?.conditions || []).find((c) => afterTargetsCond(afterName, c))
    if (sibling) {
      return gatewayIds.get(`${sibling.after}::${sibling.if}`)
    }

    // Last resort: the parent is a dangling decision (no event references it).
    const decisionBySlug = (data.decisions || []).find((d) => d.slug === afterName)
    if (decisionBySlug) {
      return gatewayIds.get(`${decisionBySlug.follows}::${decisionBySlug.description}`)
    }

    return afterName
  }

  // An inner gateway's inbound branch label lives on the outer condition pointing at it.
  // Map: inner-gateway key → that label, to attach when the gateway is created.
  const gatewayInboundLabels = new Map()
  for (const event of allExportedEvents) {
    const conds = event.conditions || []
    for (const cond of conds) {
      const successor = conds.find((c) => afterTargetsCond(c.after, cond))
      if (!successor) {
        continue
      }
      const succKey = `${successor.after}::${successor.if}`
      if (gatewayInboundLabels.has(succKey)) {
        continue
      }
      gatewayInboundLabels.set(succKey, cond.is)
    }
  }

  // Sort an event's conditions chain-root → leaf so that creating gateways
  // in this order produces a valid parent reference at each step.
  const sortConditionsRootToLeaf = (conds) => {
    const remaining = [...conds]
    const sorted = []
    while (remaining.length > 0) {
      const idx = remaining.findIndex(
        (c) => !remaining.some((c2) => c2 !== c && afterTargetsCond(c.after, c2))
      )
      const next = idx === -1 ? 0 : idx
      sorted.push(remaining.splice(next, 1)[0])
    }

    return sorted
  }

  const ensureGatewayForCondition = (cond, event) => {
    const key = `${cond.after}::${cond.if}`
    if (createdGateways.has(key)) {
      return
    }
    createdGateways.add(key)

    const afterId = resolveGatewayAfter(cond.after, event)
    const inboundLabel = gatewayInboundLabels.get(key)
    const conditionLabels = inboundLabel !== undefined
      ? [{ parentId: afterId, name: inboundLabel }]
      : undefined

    domainEvents.push({
      id: gatewayIds.get(key),
      description: cond.if,
      type: 'bpmn:ExclusiveGateway',
      laneId: event.role ? laneIds.get(event.role) : undefined,
      parents: [afterId],
      conditionLabels,
      version: 1
    })
  }

  const reverseEventInOrder = (event, ctx) => {
    for (const cond of sortConditionsRootToLeaf(event.conditions || [])) {
      ensureGatewayForCondition(cond, event)
    }
    domainEvents.push(reverseEvent(event, ctx))
  }

  for (const event of data.domainEvents || []) {
    reverseEventInOrder(
      event,
      {
        eventIds,
        laneIds,
        groupIds,
        schemaIds,
        bcIds,
        bcName: currentBcName,
        cardTypesMap,
        gatewayIds,
        afterTargetsCond
      }
    )
  }
  for (const extBc of data.externalBoundedContexts || []) {
    const isUnassigned = extBc.name === 'Unassigned'
    for (const event of extBc.domainEvents || []) {
      reverseEventInOrder(
        event,
        {
          eventIds,
          laneIds,
          groupIds,
          schemaIds,
          bcIds,
          bcName: isUnassigned ? null : extBc.name,
          cardTypesMap,
          gatewayIds,
          afterTargetsCond
        }
      )
    }
  }

  const findDecisionBySlug = (slug) => (data.decisions || []).find((d) => d.slug === slug)

  const findDecisionByDescription = (description) => (data.decisions || []).find((d) => d.description === description)

  const resolveDecisionFollows = (followsName) => {
    if (followsName === 'start') {
      return 'start'
    }
    const eventId = eventIds.get(followsName)
    if (eventId) {
      return eventId
    }
    // Prefer the precise per-slug id so a child following the second of two
    // same-named siblings targets that sibling, not the first.
    if (decisionSlugToGatewayId.has(followsName)) {
      return decisionSlugToGatewayId.get(followsName)
    }
    // Fall back to description (old exports without slugs) via the composite key.
    const parentDecision = findDecisionBySlug(followsName) || findDecisionByDescription(followsName)
    if (parentDecision) {
      return gatewayIds.get(`${parentDecision.follows}::${parentDecision.description}`)
    }

    return followsName
  }

  const resolveDecisionCardTypeId = (title) => {
    if (!cardTypesMap) {
      return undefined
    }
    if (title != null && cardTypesMap[`__title:${title}`] != null) {
      return cardTypesMap[`__title:${title}`]
    }
    if (cardTypesMap.__default__ != null) {
      return cardTypesMap.__default__
    }

    return undefined
  }

  const decisionNow = new Date().toISOString()

  const buildDecisionCards = (decision) =>
    (decision.cards || []).map((card, idx) =>
      stripUndefined({
        id: randomUUID(),
        cardTypeId: resolveDecisionCardTypeId(card.type),
        description: card.description || '',
        sortkey: generateSortkey(idx),
        created: decisionNow,
        done: false,
        version: 1
      })
    )

  // Create each decision's gateway record (or attach cards to one already built from
  // conditions[]). Ids were assigned above, so parents resolve to the right sibling.
  for (const decision of data.decisions || []) {
    const gatewayId = decisionGatewayIdByEntry.get(decision)
    const cards = buildDecisionCards(decision)

    const existing = domainEvents.find((e) => e.id === gatewayId)
    if (existing) {
      if (cards.length > 0) {
        existing.cards = cards
      }
      continue
    }

    const parentId = resolveDecisionFollows(decision.follows)
    const conditionLabels = decision.conditionLabel !== undefined
      ? [{ parentId, name: decision.conditionLabel }]
      : undefined

    domainEvents.push(stripUndefined({
      id: gatewayId,
      description: decision.description,
      type: 'bpmn:ExclusiveGateway',
      laneId: decision.role ? laneIds.get(decision.role) : undefined,
      parents: [parentId],
      conditionLabels,
      cards: cards.length > 0 ? cards : undefined,
      version: 1
    }))
  }

  // Phase 4: Bounded contexts, lanes, groups
  const boundedContexts = allBcNames.map((name) => ({
    id: resolve(bcIds, name),
    name
  }))

  const lanes = (data.roles || data.lanes || []).map((name) => ({
    id: resolve(laneIds, name),
    name
  }))

  const groups = (data.groups || []).map((group) => ({
    id: resolve(groupIds, group.name),
    name: group.name,
    startSlotX: group.startSlotX
  }))

  return {
    name: 'Imported Workflow',
    domainEvents,
    lanes,
    schemas,
    groups,
    releases: data.releases || [],
    boundedContexts
  }
}

const reverseNestedFields = (nestedFields, parent, schemaIds) => {
  if (!nestedFields) {
    return undefined
  }
  const requiredSet = new Set(parent?.required || [])
  return nestedFields.map((f) =>
    stripUndefined({
      name: f.name,
      relatedEntityId: f.relatedEntity ? schemaIds.get(f.relatedEntity) : undefined,
      cardinality: f.array ? 'one-to-many' : 'one-to-one',
      computed: f.computed ? true : undefined,
      tags: requiredSet.has(f.name) ? ['REQUIRED'] : undefined,
      fields: reverseNestedFields(f.fields, f, schemaIds)
    })
  )
}

const reverseSchema = (schema, maps) => {
  const { schemaIds, bcIds } = maps
  const newId = schemaIds.get(schema.name)

  if (schema.type === SchemaType.ENTITY || schema.type === 'ValueObject') {
    const requiredSet = new Set(schema.required || [])
    return stripUndefined({
      id: newId,
      name: schema.name,
      description: schema.description,
      type: SchemaType.ENTITY,
      boundedContext: schema.boundedContext ? bcIds.get(schema.boundedContext) : undefined,
      fields: (schema.fields || []).map((field) =>
        stripUndefined({
          name: field.name,
          description: field.description,
          cardinality: field.array ? 'one-to-many' : 'one-to-one',
          exampleData: field.exampleData,
          relatedEntityId: field.relatedEntity ? schemaIds.get(field.relatedEntity) : undefined,
          fields: reverseNestedFields(field.fields, field, schemaIds),
          tags: requiredSet.has(field.name) ? ['REQUIRED'] : undefined,
          dataType: field.dataType
        })
      )
    })
  }

  if (schema.type === SchemaType.QUERY) {
    return stripUndefined({
      id: newId,
      name: schema.name,
      description: schema.description,
      type: SchemaType.QUERY,
      cardinality: schema.array ? 'one-to-many' : 'one-to-one',
      entityId: schema.entity ? schemaIds.get(schema.entity) : undefined,
      fields: (schema.fields || []).map((field) =>
        stripUndefined({
          name: field.name,
          description: field.description,
          relatedEntityId: field.relatedEntity ? schemaIds.get(field.relatedEntity) : undefined,
          fields: reverseNestedFields(field.fields, field, schemaIds),
          type: field.isFilter ? 'filter' : undefined,
          computed: field.computed ? true : undefined
        })
      )
    })
  }

  if (schema.type === SchemaType.DOMAIN_EVENT) {
    return stripUndefined({
      id: newId,
      name: schema.name,
      type: SchemaType.DOMAIN_EVENT,
      entityId: schema.entity ? schemaIds.get(schema.entity) : undefined,
      fields: (schema.fields || []).map((field) =>
        stripUndefined({
          name: field.name,
          relatedEntityId: field.relatedEntity ? schemaIds.get(field.relatedEntity) : undefined,
          cardinality: field.array ? 'one-to-many' : 'one-to-one',
          fields: reverseNestedFields(field.fields, field, schemaIds)
        })
      )
    })
  }

  if (schema.type === SchemaType.COMMAND) {
    const requiredSet = new Set(schema.required || [])
    return stripUndefined({
      id: newId,
      name: schema.name,
      type: SchemaType.COMMAND,
      fields: (schema.fields || []).map((field) =>
        stripUndefined({
          name: field.name,
          relatedEntityId: field.relatedEntity ? schemaIds.get(field.relatedEntity) : undefined,
          cardinality: field.array ? 'one-to-many' : 'one-to-one',
          fields: reverseNestedFields(field.fields, field, schemaIds),
          tags: requiredSet.has(field.name) ? ['REQUIRED'] : undefined,
          hideInForm: field.hideInForm
        })
      )
    })
  }

  return { id: newId, ...schema }
}

// Generate lexicographically ordered sortkeys for cards within an event
const generateSortkey = (position) => {
  const base = ['n', 'u', 'x', 'z']
  if (position < base.length) {
    return base[position]
  }
  const extra = position - base.length

  return 'z' + String.fromCharCode(97 + (extra % 26))
}

const reverseEvent = (event, maps) => {
  const { eventIds, laneIds, groupIds, schemaIds, bcIds, bcName, cardTypesMap, gatewayIds, afterTargetsCond } = maps
  const targetsCond = afterTargetsCond || ((after, cond) => after === cond?.if)
  const RESERVED = new Set(['start'])

  const resolveParent = (name) => {
    if (RESERVED.has(name)) {
      return name
    }

    return eventIds.get(name) || randomUUID()
  }

  const resolveCardTypeIdForRole = (role) => {
    if (!cardTypesMap) {
      return undefined
    }
    if (role != null && cardTypesMap[role] != null) {
      return cardTypesMap[role]
    }
    if (role == null && cardTypesMap.__default__ != null) {
      return cardTypesMap.__default__
    }

    return undefined
  }

  const resolveCardTypeIdForTitle = (title) => {
    if (!cardTypesMap) {
      return undefined
    }
    if (title != null && cardTypesMap[`__title:${title}`] != null) {
      return cardTypesMap[`__title:${title}`]
    }
    if (cardTypesMap.__default__ != null) {
      return cardTypesMap.__default__
    }

    return undefined
  }

  const now = new Date().toISOString()
  const cards = []
  let cardPos = 0

  // Command card
  if (event.command) {
    cards.push(
      stripUndefined({
        id: randomUUID(),
        cardTypeId: resolveCardTypeIdForRole('Command'),
        domainModelRole: 'Command',
        description: event.command,
        schemaId: schemaIds.get(event.command),
        sortkey: generateSortkey(cardPos++),
        created: now,
        done: false,
        version: 1
      })
    )
  }

  // DomainEvent card
  if (event.domainEventSchema) {
    const deName = event.domainEventSchema?.name || (typeof event.domainEventSchema === 'string' ? event.domainEventSchema : null)
    if (deName) {
      cards.push(
        stripUndefined({
          id: randomUUID(),
          cardTypeId: resolveCardTypeIdForRole('DomainEvent'),
          domainModelRole: 'DomainEvent',
          description: event.domainEventSchema?.description || deName,
          schemaId: schemaIds.get(deName),
          sortkey: generateSortkey(cardPos++),
          created: now,
          done: false,
          version: 1
        })
      )
    }
  }

  // AggregateRoot card
  if (event.aggregateRoot) {
    cards.push(
      stripUndefined({
        id: randomUUID(),
        cardTypeId: resolveCardTypeIdForRole('AggregateRoot'),
        domainModelRole: 'AggregateRoot',
        description: event.aggregateRoot,
        schemaId: schemaIds.get(event.aggregateRoot),
        boundedContext: bcName ? bcIds.get(bcName) : undefined,
        sortkey: generateSortkey(cardPos++),
        created: now,
        done: false,
        version: 1
      })
    )
  }

  // ReadModel cards
  for (const rm of event.readModels || []) {
    cards.push(
      stripUndefined({
        id: randomUUID(),
        cardTypeId: resolveCardTypeIdForRole('ReadModel'),
        domainModelRole: 'ReadModel',
        description: rm.description || rm.name || '',
        schemaId: schemaIds.get(rm.name),
        sortkey: generateSortkey(cardPos++),
        created: now,
        done: false,
        version: 1
      })
    )
  }

  const acList = Array.isArray(event.acceptanceCriteria)
    ? event.acceptanceCriteria
    : event.acceptanceCriteria ? [event.acceptanceCriteria] : []
  for (const ac of acList) {
    cards.push(
      stripUndefined({
        id: randomUUID(),
        cardTypeId: resolveCardTypeIdForRole('GivenWhenThen'),
        domainModelRole: 'GivenWhenThen',
        description: ac,
        sortkey: generateSortkey(cardPos++),
        created: now,
        done: false,
        version: 1
      })
    )
  }

  // Custom cards (no domainModelRole)
  for (const card of event.cards || []) {
    cards.push(
      stripUndefined({
        id: randomUUID(),
        cardTypeId: resolveCardTypeIdForTitle(card.type),
        description: card.description || '',
        sortkey: generateSortkey(cardPos++),
        created: now,
        done: false,
        version: 1
      })
    )
  }

  // Resolve parents: route through gateways when conditions are present
  let parents
  let conditionLabels

  if (event.conditions && event.conditions.length > 0 && gatewayIds) {
    const conditionedAfters = new Set(event.conditions.map((c) => c.after))
    // Inner gateways are those whose `if` is referenced as `after` by another
    // condition on this same event — they're chain links, not the event's
    // direct parent. Only the leaf conditions (those whose `if` isn't an
    // `after` elsewhere) attach as parents of the event itself.
    const innerConds = new Set(
      event.conditions
        .filter((c) => event.conditions.some((c2) => targetsCond(c2.after, c)))
    )
    parents = []
    conditionLabels = []

    for (const followName of event.follows || []) {
      if (!conditionedAfters.has(followName)) {
        parents.push(resolveParent(followName))
      }
    }

    for (const cond of event.conditions) {
      if (innerConds.has(cond)) {
        continue
      }
      const key = `${cond.after}::${cond.if}`
      const gwId = gatewayIds.get(key)
      if (gwId && !parents.includes(gwId)) {
        parents.push(gwId)
      }
      // Recreate a label entry whenever `is` is present — including an explicitly
      // cleared one (""). An absent `is` leaves the branch unlabeled ("[Label]").
      if (cond.is !== undefined) {
        conditionLabels.push({ parentId: gwId, name: cond.is })
      }
    }

    if (conditionLabels.length === 0) {
      conditionLabels = undefined
    }
  } else if (event.conditionLabels) {
    parents = event.follows?.map(resolveParent)
    conditionLabels = event.conditionLabels.map((cl) => ({
      parentId: cl.parent ? resolveParent(cl.parent) : cl.parent,
      name: cl.name
    }))
  } else {
    parents = event.follows?.map(resolveParent)
  }

  return stripUndefined({
    id: eventIds.get(event.slug || event.event),
    description: event.event,
    laneId: event.role ? laneIds.get(event.role) : undefined,
    groupId: event.group ? groupIds.get(event.group) : undefined,
    type: 'bpmn:Task',
    conditionLabel: event.conditionLabel,
    conditionLabels,
    parents,
    version: 1,
    cards: cards.length > 0 ? cards : undefined
  })
}

const stripUndefined = (obj) => {
  const result = {}
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) {
      result[key] = value
    }
  }
  return result
}
